import React, { useCallback, Fragment, useState } from 'react';
import { useCombobox, UseComboboxState, UseComboboxStateChangeOptions } from 'downshift';
import Fuse from 'fuse.js';
import {
  Dialog,
  FormLabel,
  List,
  ListItem,
  Input,
  usePopover,
  useBreakpoint,
  Spinner,
} from '@spaceshipapp/cambridge';
import { Card } from '../Select/styled';
import { setFixedScrollPosition } from '../../utils/helpers';
import {
  cardStyle,
  mobileListStyle,
  comboboxStyle,
  inputStyle,
  StyledSheet,
  SubLabel,
  Placeholder,
  Wrapper,
} from './styled';

const stateReducer = <Item extends unknown>(
  state: UseComboboxState<Item>,
  actionAndChanges: UseComboboxStateChangeOptions<Item>,
) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { type, changes, props } = actionAndChanges;
  switch (type) {
    case useCombobox.stateChangeTypes.FunctionCloseMenu:
    case useCombobox.stateChangeTypes.InputBlur:
    case useCombobox.stateChangeTypes.InputKeyDownEscape:
      return {
        ...changes,
        // keep valid value in input field
        inputValue: props.itemToString(state.selectedItem) || '',
      };
    default:
      return changes;
  }
};

export interface AutocompleteProps<Item> extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  sync?: boolean;
  label?: string;
  selectedItem: Item | null;
  isError?: boolean;
  isLoading?: boolean;
  items: Item[];
  onSelectedItemChange: (selectedItem?: Item | null) => void;
  onInputValueChange?: (inputValue: string) => void;
  noItemsLabel?: string;
  searchOptions?: Fuse.IFuseOptions<Item>;
  itemToString?: (item: Item | null) => string;
  itemToKey?: (item: Item) => string;
  itemToSubLabel?: (item: Item) => string;
  children?: React.ReactNode;
}

const Autocomplete = <Item extends unknown>(props: AutocompleteProps<Item>): React.ReactElement => {
  const {
    id,
    sync,
    label,
    onSelectedItemChange,
    onInputValueChange,
    selectedItem,
    items,
    isLoading = false,
    noItemsLabel = '',
    onFocus,
    onBlur,
    searchOptions = {},
    itemToString = (item) => item as string,
    itemToKey = (item) => item as string,
    itemToSubLabel: itemToSubLabel,
    ...remain
  } = props;

  const { breakpoint } = useBreakpoint('mobile');
  const fuse = new Fuse(items, searchOptions);
  const [inputValue, setInputValue] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const dropdownOptions =
    !sync && isDirty && inputValue ? fuse.search(inputValue).map((result) => result.item) : items;

  const {
    triggerProps: { ref: triggerRef, onClick: _, ...trigger },
    contentProps,
  } = usePopover<HTMLElement, HTMLDivElement>();

  const mobileListRef = useCallback((node) => {
    const isOpen = !!node;
    setFixedScrollPosition(isOpen);
  }, []);

  const {
    isOpen,
    closeMenu,
    getLabelProps,
    getInputProps,
    toggleMenu,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    getComboboxProps,
  } = useCombobox({
    id,
    items: dropdownOptions,
    itemToString,
    onInputValueChange: ({ inputValue }) => {
      if (!isDirty) setIsDirty(true);
      setInputValue(inputValue || '');

      if (onInputValueChange) {
        onInputValueChange(inputValue || '');
      }
    },
    selectedItem,
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) onSelectedItemChange(selectedItem);
    },
    onIsOpenChange: ({ isOpen }) => {
      if (!isOpen) setIsDirty(false);
    },
    stateReducer,
  });

  const showDropdown = isOpen && (noItemsLabel || dropdownOptions.length > 0);

  // expand menu to full screen on mobile
  if (isOpen && breakpoint === 'mobile') {
    return (
      <Fragment>
        <div {...getComboboxProps()}>
          <span {...getLabelProps()} hidden />
        </div>
        <Dialog title={label} onClose={closeMenu} className={cardStyle} {...getMenuProps()}>
          <Input
            appendAddon={isLoading && <Spinner height={20} width={20} />}
            {...remain}
            {...getInputProps({
              ...trigger,
              onBlur: (e) => {
                if (onBlur) onBlur(e);
              },
            })}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
          <List ref={mobileListRef} className={mobileListStyle}>
            {dropdownOptions.length > 0 ? (
              dropdownOptions.map((item, index: number) => (
                <ListItem
                  key={itemToKey(item)}
                  actionable
                  active={highlightedIndex === index}
                  {...getItemProps({
                    item,
                    index,
                  })}
                >
                  <span>{itemToString(item)}</span>
                  {itemToSubLabel && <SubLabel>{itemToSubLabel(item)}</SubLabel>}
                </ListItem>
              ))
            ) : (
              <Placeholder>{noItemsLabel}</Placeholder>
            )}
          </List>
        </Dialog>
      </Fragment>
    );
  }

  return (
    <Wrapper>
      <div className={comboboxStyle} {...getComboboxProps()}>
        {label && <FormLabel {...getLabelProps()}>{label}</FormLabel>}
        <Input
          className={inputStyle}
          appendAddon={isLoading && <Spinner height={20} width={20} />}
          {...remain}
          {...trigger}
          {...getInputProps({
            ref: triggerRef as React.LegacyRef<HTMLInputElement>,
            onFocus: (e) => {
              if (!isOpen) toggleMenu();
              if (onFocus) onFocus(e);
            },
            onBlur: (e) => {
              if (onBlur) onBlur(e);
            },
          })}
        />
      </div>
      <StyledSheet
        {...getMenuProps({
          ...(showDropdown ? contentProps : {}),
        })}
      >
        {showDropdown && (
          <Card>
            <List>
              {dropdownOptions.length > 0 ? (
                dropdownOptions.map((item, index: number) => (
                  <ListItem
                    key={itemToKey(item)}
                    active={highlightedIndex === index}
                    {...getItemProps({
                      item,
                      index,
                    })}
                  >
                    <span>{itemToString(item)}</span>
                    {itemToSubLabel && <SubLabel>{itemToSubLabel(item)}</SubLabel>}
                  </ListItem>
                ))
              ) : (
                <Placeholder>{noItemsLabel}</Placeholder>
              )}
            </List>
          </Card>
        )}
      </StyledSheet>
    </Wrapper>
  );
};

export default Autocomplete;
